import { useTranslation } from 'react-i18next';

import { AppConfigService } from 'features/app-config/services';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  useCallDeafParticipantHandlers,
  useCallHearingParticipantHandlers,
} from 'features/call/call-accepted/hooks';
import { PhoneNumberStatus } from 'features/call/call-base/interfaces';
import { CallDaoService } from 'features/call/call-base/services';
import { isDeafToHearingSelector } from 'features/call/call-base/store/selectors';
import { handleError, showNotification } from 'features/notification/store';
import {
  deafIsLoadingSelector,
  setDeafIsLoading,
} from 'features/call/call-deaf/store';
import { isVoiceSessionLoadingSelector } from 'features/voice-session/store';

export const useSpawnParticipantCall = () => {
  const dispatch = useAppDispatch();
  const isDeafToHearing = useAppSelector(isDeafToHearingSelector);
  const isDeafLoading = useAppSelector(deafIsLoadingSelector);
  const isVoiceSessionLoading = useAppSelector(isVoiceSessionLoadingSelector);

  const { t } = useTranslation();
  const { t: addCallee } = useTranslation('translation', {
    keyPrefix: 'addCallee',
  });

  const { handleHearingSpawnNewCall } = useCallHearingParticipantHandlers();
  const { handleDeafConnectNewCall } = useCallDeafParticipantHandlers();

  const handleDeafSpawnNewCall = async (phoneNumber: string) => {
    dispatch(setDeafIsLoading(true));
    if (AppConfigService.isCheckOnRegistrationNeed) {
      handleDeafConnectNewCall(phoneNumber);
      return;
    }

    try {
      const { Type: registrationType } =
        await CallDaoService.phoneNumberRegistration(phoneNumber);

      if (registrationType !== PhoneNumberStatus.NOT_REGISTERED) {
        handleDeafConnectNewCall(phoneNumber);
      } else {
        dispatch(
          showNotification({
            severity: 'warning',
            title: t('phoneNumberNotRegistered'),
          })
        );
      }
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'handleDeafSpawnNewCall',
        })
      );
      dispatch(setDeafIsLoading(false));
    }
  };

  if (isDeafToHearing) {
    return {
      handleSpawnNewCall: handleHearingSpawnNewCall,
      isLoading: isVoiceSessionLoading,
      title: addCallee('addHearingCallee'),
    };
  }

  return {
    handleSpawnNewCall: handleDeafSpawnNewCall,
    isLoading: isDeafLoading,
    title: addCallee('addDeafCallee'),
  };
};
